// The building's energy on an ordered property, as Sections 3 to 5 of the Solar Opportunity Report
// (10-serverless/120-pack/render.py s3, s4, s5; Neil, 11 Oct 2026: "the PDF is just an export of the
// sections in the details page"). From the API's energy block (100-api common/energy.py): the
// building's energy records year by year, what it spends today, and a five-year forecast. Never names
// where the figures come from.
//
// Private commercial (BUILD-PLAN-PRIVATE-COMMERCIAL.md PC5.3): where energy.basis is "benchmark" the
// use is estimated from the building's size and use. Section 3 shows an "Estimated energy use" card
// and how to firm it up instead of the latest actual use and what the records tell; Section 5 shows
// the one estimated year (labelled estimated, no "Actual" divider) and the forecast from it. Rows
// with nothing on record (as-run rating, renewables, years on record) are left out.
//
//   EnergyRecords   Section 3: energy records (latest use, as built, what the records tell an installer)
//   EnergySpend     Section 4: what the building spends on energy today (figures, history, the callout)
//   EnergyHistory   Section 5: N years of electricity use, and the next five (chart and forecast table)
import type { ReactNode } from "react";
import { BadgeCheck, BarChart3, ChartLine, Gauge, Hammer, PoundSterling } from "lucide-react";
import type { EnergyBlock, OwnedBuildingDetail } from "../api";
import { SectionHead } from "./PackSections";
import { cap, estimateWords, gbp, mwh, num, occupant } from "./packFormat";

/** Bars for the actual (or estimated) years, then the forecast range (flat to +5%) with the central line. */
function UseChart({ e }: { e: EnergyBlock }) {
  const years = e.years;
  const fc = e.forecast?.years ?? [];
  const n = years.length + fc.length;
  if (!n) return null;
  const max = Math.max(...years.map((y) => y.kwh), ...fc.map((f) => f.high.kwh)) * 1.1;
  const W = 720,
    H = 240,
    L = 56,
    B = 40,
    T = 16;
  const slot = (W - L - 8) / n;
  const bw = Math.min(34, slot * 0.68);
  const y = (v: number) => T + (H - T - B) * (1 - v / max);
  const x = (i: number) => L + slot * i + (slot - bw) / 2;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max);
  const divide = x(years.length) - (slot - bw) / 2;
  // Estimated years (private: one, from size and use) are labelled as such, never "Actual"
  const estimated = e.basis === "benchmark" || years.some((yr) => yr.estimated);
  return (
    <svg className="eo-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Electricity use a year, ${estimated ? "estimated" : "actual"} then forecast`}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={L} x2={W - 8} y1={y(t)} y2={y(t)} className="eo-grid" />
          <text x={L - 6} y={y(t) + 4} className="eo-axis" textAnchor="end">
            {Math.round(t / 1000)}
          </text>
        </g>
      ))}
      <text x={8} y={T + 4} className="eo-axis">
        MWh
      </text>
      {years.map((yr, i) => (
        <g key={yr.year}>
          <rect x={x(i)} y={y(yr.kwh)} width={bw} height={y(0) - y(yr.kwh)} rx={3} className={yr.unusual ? "eo-bar is-unusual" : yr.estimated ? "eo-bar is-estimated" : "eo-bar"}>
            <title>{`${yr.year}: ${mwh(yr.kwh)}, ${gbp(yr.cost)}${yr.unusual ? " (unusual year: left out of the trend)" : yr.estimated ? " (estimated)" : ""}`}</title>
          </rect>
          <text x={x(i) + bw / 2} y={H - B + 14} className="eo-axis" textAnchor="end" transform={`rotate(-40 ${x(i) + bw / 2} ${H - B + 14})`}>
            {yr.year}
          </text>
        </g>
      ))}
      {/* Actual (or estimated) on the left of the line, forecast on the right (as the report's chart) */}
      {fc.length > 0 && years.length > 0 && (
        <>
          <line x1={divide} x2={divide} y1={T} y2={y(0)} className="eo-divide" />
          <text x={divide - 4} y={T + 4} className="eo-axis" textAnchor="end">
            {estimated ? "← Estimated" : "← Actual"}
          </text>
          <text x={divide + 4} y={T + 4} className="eo-axis">
            Forecast →
          </text>
        </>
      )}
      {fc.map((f, j) => {
        const i = years.length + j;
        return (
          <g key={f.year}>
            <rect x={x(i)} y={y(f.high.kwh)} width={bw} height={Math.max(1, y(f.low.kwh) - y(f.high.kwh))} rx={3} className="eo-range">
              <title>{`${f.year}: ${mwh(f.low.kwh)} to ${mwh(f.high.kwh)}`}</title>
            </rect>
            <line x1={x(i)} x2={x(i) + bw} y1={y(f.central.kwh)} y2={y(f.central.kwh)} className="eo-central" />
            <text x={x(i) + bw / 2} y={H - B + 14} className="eo-axis" textAnchor="end" transform={`rotate(-40 ${x(i) + bw / 2} ${H - B + 14})`}>
              {f.year}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** A label and value table (the report's "kv" tables), leaving out rows with nothing to say. */
export function KV({ rows }: { rows: [string, ReactNode][] }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== "" && v !== false);
  return (
    <dl className="pk-kv">
      {shown.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Section 3: energy records. */
export function EnergyRecords({ b }: { b: OwnedBuildingDetail }) {
  const e = b.energy;
  const t = e?.today;
  if (!e || !t) return null;
  const p = b.property;
  const occ = occupant(p.sector);
  const years = e.years;
  const ratings = e.ratings;
  const design = ratings?.design;
  const usual = years.filter((y) => !y.unusual);
  // Private: estimated from the building's size and use, with no energy records behind it
  if (e.basis === "benchmark") return <EstimatedRecords b={b} />;

  // What the records tell an installer (render.py "tells")
  const tells: ReactNode[] = [];
  if (usual.length >= 2) {
    const lo = usual.reduce((a, y) => (y.kwh < a.kwh ? y : a));
    const hi = usual.reduce((a, y) => (y.kwh > a.kwh ? y : a));
    tells.push(
      <>
        <b>
          Use has ranged {mwh(lo.kwh)} ({lo.year}) to {mwh(hi.kwh)} ({hi.year})
        </b>
        ; the latest year is {mwh(years[years.length - 1].kwh)}.
      </>,
    );
  }
  if (e.changePct != null)
    tells.push(
      <>
        <b>
          {e.changePct > 0 ? "Rising" : "Falling"} {Math.abs(e.changePct)}% a year
        </b>{" "}
        over the last three years{e.changePct > 0 ? ": a growing load makes solar more valuable" : ""}.
      </>,
    );
  if (years.some((y) => y.unusual))
    tells.push(
      <>
        <b>Unusual years</b> (COVID closures) are greyed out and left out of the trends.
      </>,
    );
  // Only where the records say either way (nothing on record: no line)
  if (e.renewablesRecorded != null)
    tells.push(
      e.renewablesRecorded ? (
        <>
          <b>On-site generation is recorded</b>: check what's installed.
        </>
      ) : (
        <>
          <b>No on-site generation recorded</b>: nothing on the energy record to work around.
        </>
      ),
    );

  return (
    <section className="card eo" aria-labelledby="pk-records">
      <SectionHead
        id="pk-records"
        kicker="Section 3"
        icon={Gauge}
        title="Energy records"
        sub={
          e.estimated
            ? `There are no actual energy figures on record for ${occ}: its electricity use is estimated for its type and size.`
            : `${cap(occ)} reports its actual energy use every year, so these are measured figures, not modelled ones.`
        }
      />
      <div className="about-two">
        <div className="pk-panel">
          <h3 className="pk-sub">
            <Gauge size={16} aria-hidden="true" /> {e.estimated ? "Energy use (estimated)" : "Latest actual energy use"}
          </h3>
          <KV
            rows={[
              ["Year", years.length ? years[years.length - 1].year : null],
              [
                "Efficiency rating (as run)",
                ratings?.asRun ? (
                  <>
                    <span className={`eo-band is-${ratings.asRun}`}>{ratings.asRun}</span> ({ratings.asRunScore}); 100 = typical
                  </>
                ) : null,
              ],
              [
                "Electricity",
                <>
                  <b>{num(t.electricityKwh)} kWh</b>
                  {p.floorM2 ? ` (${Math.round(t.electricityKwh / p.floorM2)} kWh/m²)` : ""}
                </>,
              ],
              ["Gas", t.gasKwh ? `${num(t.gasKwh)} kWh` : "Not reported"],
              ["On-site renewable electricity", e.renewablesRecorded == null ? null : e.renewablesRecorded ? "Recorded" : "0"],
              ["Years on record", years.length ? String(years.length) : null],
            ]}
          />
        </div>
        <div className="pk-panel">
          <h3 className="pk-sub">
            <Hammer size={16} aria-hidden="true" /> As built
          </h3>
          {design ? (
            <KV
              rows={[
                [
                  "Efficiency rating (as built)",
                  design.band ? (
                    <>
                      <span className={`eo-band is-${design.band}`}>{design.band}</span>
                      {design.score ? ` (${Math.round(design.score)})` : ""}
                    </>
                  ) : null,
                ],
                ["Main heating", design.heating],
                ["Building services", design.ventilation],
                ["Air conditioning", design.aircon ? "Yes" : design.aircon === false ? "No" : null],
              ]}
            />
          ) : (
            <p className="pk-notfound">No as-built rating found for this building.</p>
          )}
        </div>
      </div>
      <div className="about-list pk-panel">
        <h3>
          <BadgeCheck size={16} aria-hidden="true" /> What the records tell an installer
        </h3>
        <ul>
          {tells.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * Section 3 for a building with no energy records (private): its estimated use, what the estimate
 * rests on in neutral words (size, use, heating, cooling) and how to firm it up once in touch.
 */
function EstimatedRecords({ b }: { b: OwnedBuildingDetail }) {
  const e = b.energy!;
  const t = e.today!;
  const p = b.property;
  const design = e.ratings?.design;
  const floor = p.floorM2 ?? t.floorM2;
  const units = p.units ?? [];
  return (
    <section className="card eo" aria-labelledby="pk-records">
      <SectionHead
        id="pk-records"
        kicker="Section 3"
        icon={Gauge}
        title="Energy use"
        sub={`There are no energy figures on record for ${occupant(p.sector)}: its electricity use is estimated from its size and use.`}
      />
      <div className="about-two">
        <div className="pk-panel">
          <h3 className="pk-sub">
            <Gauge size={16} aria-hidden="true" /> Estimated energy use
          </h3>
          <KV
            rows={[
              [
                "Electricity a year",
                <>
                  <b>{num(t.electricityKwh)} kWh</b>
                  {floor ? ` (${Math.round(t.electricityKwh / floor)} kWh/m²)` : ""}
                </>,
              ],
              ["Gas a year", t.gasKwh ? `${num(t.gasKwh)} kWh` : null],
              ["Floor area", floor ? `${num(floor)} m²` : null],
              ["Use", p.buildingType],
              ["Main heating", design?.heating ?? null],
              ["Air conditioning", design?.aircon ? "Yes" : design?.aircon === false ? "No" : null],
              ["How close", estimateWords(p)],
            ]}
          />
        </div>
        <div className="pk-panel">
          <h3 className="pk-sub">
            <Hammer size={16} aria-hidden="true" /> As built
          </h3>
          {design?.band ? (
            <KV
              rows={[
                [
                  "Efficiency rating (as built)",
                  <>
                    <span className={`eo-band is-${design.band}`}>{design.band}</span>
                    {design.score ? ` (${Math.round(design.score)})` : ""}
                  </>,
                ],
                ["Building services", design.ventilation],
              ]}
            />
          ) : (
            <p className="pk-notfound">No as-built rating found for this building.</p>
          )}
        </div>
      </div>
      <div className="about-list pk-panel">
        <h3>
          <BadgeCheck size={16} aria-hidden="true" /> How to firm it up
        </h3>
        <ul>
          <li>
            <b>Half-hourly meter data from the bill payer</b>, once you're in touch, replaces this estimate with the building's own use and daily pattern.
          </li>
          <li>
            <b>A recent electricity bill</b> confirms the annual use and the price they pay.
          </li>
          {units.length > 1 && (
            <li>
              <b>{units.length} businesses share this building</b> (Section 2): each may have its own supply, so ask each bill payer.
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}

/** Section 4: what the building spends on energy today. */
export function EnergySpend({ b }: { b: OwnedBuildingDetail }) {
  const e = b.energy;
  const t = e?.today;
  if (!e || !t) return null;
  const occ = occupant(b.property.sector);
  const pence = e.model?.assumptions.importPence ?? e.prices.importPence;
  return (
    <section className="card eo" aria-labelledby="pk-spend">
      <SectionHead
        id="pk-spend"
        kicker="Section 4"
        icon={PoundSterling}
        title="What the building spends on energy today"
        sub={`${e.estimated ? "Estimated" : "Actual"} electricity use priced at ${pence}p/kWh. The building's own contract prices aren't public: ask for a recent bill at the survey.`}
      />
      <div className="eo-stats">
        <div>
          <b>{gbp(t.electricity)}</b>
          <span>
            electricity a year ({num(t.electricityKwh)} kWh × {pence}p)
          </span>
        </div>
        <div>
          <b>{t.gas ? gbp(t.gas) : "—"}</b>
          <span>gas a year</span>
        </div>
        <div>
          <b>{gbp(t.total)}</b>
          <span>total energy a year</span>
        </div>
        <div>
          <b>{t.perM2 != null ? `£${t.perM2}` : "—"}</b>
          <span>energy per m² of floor a year</span>
        </div>
      </div>
      {e.years.length > 0 && (
        <div className="eo-block">
          <h3>{e.basis === "benchmark" ? "Estimated year" : "Recent history"}</h3>
          <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Electricity kWh</th>
                  <th>At {pence}p today</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {e.years.map((y) => (
                  <tr key={y.year} className={y.unusual ? "is-muted" : undefined}>
                    <td>{y.year}</td>
                    <td>{num(y.kwh)}</td>
                    <td>{gbp(y.cost)}</td>
                    <td>{y.unusual ? "COVID closures (excluded)" : y.estimated ? "Estimated" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="pk-callout">
        <PoundSterling size={16} aria-hidden="true" />
        <span>
          Every 10 MWh {occ} stops buying saves about <b>{gbp((10000 * pence) / 100)}</b> a year at today's prices.
        </span>
      </div>
    </section>
  );
}

/** Section 5: years of electricity use, and the next five. */
export function EnergyHistory({ b }: { b: OwnedBuildingDetail }) {
  const e = b.energy;
  if (!e?.today || (!e.years.length && !e.forecast)) return null;
  const a = e.model?.assumptions;
  const fc = e.forecast;
  // Private: one estimated year (no invented history), and the forecast runs from it
  const estimated = e.basis === "benchmark";
  return (
    <section className="card eo" aria-labelledby="pk-history">
      <SectionHead
        id="pk-history"
        kicker="Section 5"
        icon={ChartLine}
        title={
          estimated
            ? "Estimated electricity use, and the next five years"
            : `${e.years.length} year${e.years.length === 1 ? "" : "s"} of electricity use, and the next five`
        }
        sub={
          estimated
            ? "A year's electricity estimated from the building's size and use, then a five-year forecast from it."
            : "Annual electricity from every year on record, then a five-year forecast."
        }
      />
      <div className="eo-block">
        <UseChart e={e} />
        <p className="eo-legend muted">
          {estimated ? (
            <>
              <span className="eo-key is-estimated" /> Estimated
            </>
          ) : (
            <>
              <span className="eo-key is-actual" /> Actual
              <span className="eo-key is-unusual" /> Unusual year (excluded)
            </>
          )}
          <span className="eo-key is-range" /> Forecast range (flat to +5%/yr) <span className="eo-key is-central" /> Central forecast (+2%/yr)
        </p>
      </div>
      {fc && (
        <div className="eo-block">
          <h3>
            <BarChart3 size={16} aria-hidden="true" className="pk-h3-ico" /> Five-year forecast: electricity and cost
          </h3>
          <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Low: flat</th>
                  <th>Cost</th>
                  <th>Central: +2%/yr</th>
                  <th>Cost</th>
                  <th>High: +5%/yr</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {fc.years.map((f) => (
                  <tr key={f.year}>
                    <td>{f.year}</td>
                    <td>{num(f.low.kwh)}</td>
                    <td>{gbp(f.low.cost)}</td>
                    <td>{num(f.central.kwh)}</td>
                    <td>{gbp(f.central.cost)}</td>
                    <td>{num(f.high.kwh)}</td>
                    <td>{gbp(f.high.cost)}</td>
                  </tr>
                ))}
                <tr className="eo-total">
                  <td>5 years</td>
                  <td />
                  <td>{gbp(fc.totals.low)}</td>
                  <td />
                  <td>{gbp(fc.totals.central)}</td>
                  <td />
                  <td>{gbp(fc.totals.high)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="muted eo-note">
            Costs include price rises of {a?.inflationPct ?? e.prices.inflationPct}% a year from {a?.importPence ?? e.prices.importPence}p/kWh.
          </p>
        </div>
      )}
    </section>
  );
}

